import { z } from "zod";
import { api } from "../helpers/apiClient";

// ============================================================================
// SCHEMAS
// ============================================================================

export const ClickHouseQueryDetailSchema = z.object({
  id: z.string(),
  query: z.string(),
  tables: z.array(z.string()),
  session_id: z.string().nullable().optional(),
  trace_id: z.string(),
  user_id: z.string().nullable().optional(),
  tenant: z.string(),
  executed_at: z.string(),
  duration_ms: z.number().nullable().optional(),
  status: z.string(),
  error_message: z.string().nullable().optional(),
  result_preview: z.array(z.record(z.unknown())).nullable().optional(),
});

export const LangfuseQueriesResponseSchema = z.object({
  queries: z.array(ClickHouseQueryDetailSchema),
  total_pages: z.number(),
  current_page: z.number(),
  has_more: z.boolean(),
  langfuse_project_id: z.string().nullable().optional(),
});

// ============================================================================
// INFERRED TYPES
// ============================================================================

export type ClickHouseQueryDetail = z.infer<typeof ClickHouseQueryDetailSchema>;
export type LangfuseQueriesResponse = z.infer<typeof LangfuseQueriesResponseSchema>;

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const getLangfuseQueries = async (params?: {
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}): Promise<LangfuseQueriesResponse> => {
  const queryParams: Record<string, string | number> = {};
  if (params?.start_date) queryParams.start_date = params.start_date;
  if (params?.end_date) queryParams.end_date = params.end_date;
  if (params?.page) queryParams.page = params.page;
  if (params?.limit) queryParams.limit = params.limit;

  const result = await api.get<LangfuseQueriesResponse>(
    "/api/langfuse/queries",
    queryParams,
    0,
    LangfuseQueriesResponseSchema
  );

  if (!result) {
    throw new Error("Failed to fetch Langfuse queries");
  }

  return result;
};

// ============================================================================
// PROMPT SCHEMAS
// ============================================================================

export const LangfusePromptConfigSchema = z.object({
  model: z.string().nullable().optional(),
  temperature: z.number().nullable().optional(),
  max_tokens: z.number().nullable().optional(),
  top_p: z.number().nullable().optional(),
  frequency_penalty: z.number().nullable().optional(),
  presence_penalty: z.number().nullable().optional(),
});

export const LangfusePromptSchema = z.object({
  name: z.string(),
  version: z.number(),
  prompt: z.string(),
  type: z.string(),
  labels: z.array(z.string()),
  tags: z.array(z.string()),
  config: LangfusePromptConfigSchema.nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const LangfusePromptsResponseSchema = z.object({
  prompts: z.array(LangfusePromptSchema),
  total: z.number(),
});

// ============================================================================
// PROMPT TYPES
// ============================================================================

export type LangfusePromptConfig = z.infer<typeof LangfusePromptConfigSchema>;
export type LangfusePrompt = z.infer<typeof LangfusePromptSchema>;
export type LangfusePromptsResponse = z.infer<typeof LangfusePromptsResponseSchema>;

// ============================================================================
// PROMPT API FUNCTIONS
// ============================================================================

export const getLangfusePrompts = async (params?: {
  name?: string;
  label?: string;
  tag?: string;
}): Promise<LangfusePromptsResponse> => {
  const queryParams: Record<string, string> = {};
  if (params?.name) queryParams.name = params.name;
  if (params?.label) queryParams.label = params.label;
  if (params?.tag) queryParams.tag = params.tag;

  const result = await api.get<LangfusePromptsResponse>(
    "/api/langfuse/prompts",
    queryParams,
    0,
    LangfusePromptsResponseSchema
  );

  if (!result) {
    throw new Error("Failed to fetch Langfuse prompts");
  }

  return result;
};
